import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Code,
  Webhook,
  Link2,
  Copy,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Play,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function IntegrationDocs() {
  const { user, getAuthHeaders } = useAuth();
  const [expandedSection, setExpandedSection] = useState("referral");
  const [testPayload, setTestPayload] = useState(JSON.stringify({
    event_type: "transaction.settled",
    event_id: "evt_test_123",
    timestamp: new Date().toISOString(),
    data: {
      maaly_tx_id: "mtx_test_" + Date.now(),
      maaly_merchant_id: "maaly_0001",
      volume: 1000,
      volume_currency: "USD",
      fee: 29,
      fee_currency: "USD",
      fee_eligible_for_referral: true,
      timestamp: new Date().toISOString()
    }
  }, null, 2));
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handleTestWebhook = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const payload = JSON.parse(testPayload);
      const response = await fetch(`${API_URL}/api/admin/webhook-test`, {
        method: "POST",
        credentials: "include",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      setTestResult(result);
      
      if (result.processed) {
        toast.success("Webhook processed successfully!");
      } else {
        toast.error("Webhook processing failed");
      }
    } catch (error) {
      setTestResult({ error: error.message });
      toast.error("Invalid JSON or request failed");
    } finally {
      setTesting(false);
    }
  };

  const sections = [
    {
      id: "referral",
      title: "Referral Link Attribution",
      icon: Link2,
      content: (
        <div className="space-y-4">
          <p className="text-slate-300">
            When a potential merchant clicks an affiliate's referral link, they arrive at Maaly's signup page with a <code className="px-1.5 py-0.5 rounded bg-white/5 text-blue-400">ref</code> parameter.
          </p>
          
          <div className="p-4 rounded-lg bg-background border border-border">
            <p className="text-sm text-slate-400 mb-2">Example Referral URL</p>
            <code className="text-sm text-emerald-400">
              https://maaly.io/signup?ref=ABC123&utm_source=twitter
            </code>
          </div>

          <h4 className="text-foreground font-semibold mt-6">Integration Steps</h4>
          <ol className="space-y-3 text-slate-300">
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-sm flex-shrink-0">1</span>
              <span>Capture the <code className="px-1 py-0.5 rounded bg-white/5 text-blue-400">ref</code> parameter when merchant lands on signup</span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-sm flex-shrink-0">2</span>
              <span>Store it in a cookie (30 day expiry recommended) or session</span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-sm flex-shrink-0">3</span>
              <span>Include the ref code when creating the merchant record</span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-sm flex-shrink-0">4</span>
              <span>Send <code className="px-1 py-0.5 rounded bg-white/5 text-blue-400">merchant.created</code> webhook with the ref code</span>
            </li>
          </ol>
        </div>
      ),
    },
    {
      id: "webhooks",
      title: "Webhook Events",
      icon: Webhook,
      content: (
        <div className="space-y-6">
          <p className="text-slate-300">
            Send webhooks to <code className="px-1.5 py-0.5 rounded bg-white/5 text-blue-400">{API_URL}/api/webhooks/maaly</code>
          </p>

          <div className="p-4 rounded-lg bg-background border border-border">
            <p className="text-sm text-slate-400 mb-2">Required Headers</p>
            <code className="text-sm text-slate-300 block">Content-Type: application/json</code>
            <code className="text-sm text-slate-300 block">X-Maaly-Signature: hmac_sha256_signature</code>
          </div>

          <h4 className="text-foreground font-semibold">Event: merchant.created</h4>
          <pre className="p-4 rounded-lg bg-background border border-border overflow-x-auto text-sm">
{`{
  "event_type": "merchant.created",
  "event_id": "evt_abc123",
  "timestamp": "2024-01-15T10:30:00Z",
  "data": {
    "maaly_merchant_id": "maaly_0001",
    "name": "Coffee Shop Pro",
    "website": "https://coffeeshop.io",
    "country": "US",
    "industry": "Food & Beverage",
    "status": "signed_up",
    "ref_code": "ABC123"
  }
}`}
          </pre>

          <h4 className="text-foreground font-semibold">Event: merchant.status_updated</h4>
          <pre className="p-4 rounded-lg bg-background border border-border overflow-x-auto text-sm">
{`{
  "event_type": "merchant.status_updated",
  "event_id": "evt_def456",
  "timestamp": "2024-01-16T14:00:00Z",
  "data": {
    "maaly_merchant_id": "maaly_0001",
    "status": "active"
  }
}`}
          </pre>

          <h4 className="text-foreground font-semibold">Event: transaction.settled</h4>
          <pre className="p-4 rounded-lg bg-background border border-border overflow-x-auto text-sm">
{`{
  "event_type": "transaction.settled",
  "event_id": "evt_ghi789",
  "timestamp": "2024-01-17T09:15:00Z",
  "data": {
    "maaly_tx_id": "mtx_abc123",
    "maaly_merchant_id": "maaly_0001",
    "volume": 1000.00,
    "volume_currency": "USD",
    "fee": 29.00,
    "fee_currency": "USD",
    "fee_eligible_for_referral": true,
    "timestamp": "2024-01-17T09:15:00Z"
  }
}`}
          </pre>
        </div>
      ),
    },
    {
      id: "signature",
      title: "Webhook Signature",
      icon: Code,
      content: (
        <div className="space-y-4">
          <p className="text-slate-300">
            All webhooks should be signed using HMAC-SHA256 with the shared secret.
          </p>

          <div className="p-4 rounded-lg bg-background border border-border">
            <p className="text-sm text-slate-400 mb-2">Python Example</p>
            <pre className="text-sm text-slate-300 overflow-x-auto">
{`import hmac
import hashlib

def sign_webhook(payload: bytes, secret: str) -> str:
    return hmac.new(
        secret.encode(),
        payload,
        hashlib.sha256
    ).hexdigest()

# Usage
payload = json.dumps(event_data).encode()
signature = sign_webhook(payload, webhook_secret)

# Send with header
headers = {
    "Content-Type": "application/json",
    "X-Maaly-Signature": signature
}`}
            </pre>
          </div>

          <div className="p-4 rounded-lg bg-background border border-border">
            <p className="text-sm text-slate-400 mb-2">Node.js Example</p>
            <pre className="text-sm text-slate-300 overflow-x-auto">
{`const crypto = require('crypto');

function signWebhook(payload, secret) {
  return crypto
    .createHmac('sha256', secret)
    .update(payload)
    .digest('hex');
}

// Usage
const payload = JSON.stringify(eventData);
const signature = signWebhook(payload, webhookSecret);

// Send with header
const headers = {
  'Content-Type': 'application/json',
  'X-Maaly-Signature': signature
};`}
            </pre>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <BrandLogo className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            {user ? (
              <Link to={user.role === "admin" ? "/admin" : "/app"}>
                <Button>Dashboard</Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button>Sign In</Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">Integration Guide</h1>
          <p className="text-lg text-slate-400">
            Everything you need to integrate the Maaly affiliate program with your merchant platform.
          </p>
        </div>

        {/* Documentation Sections */}
        <div className="space-y-4 mb-12">
          {sections.map((section) => (
            <div
              key={section.id}
              className="bg-card border border-border rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setExpandedSection(expandedSection === section.id ? "" : section.id)}
                className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-lg font-semibold text-foreground">{section.title}</span>
                </div>
                {expandedSection === section.id ? (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                )}
              </button>
              {expandedSection === section.id && (
                <div className="px-6 pb-6 border-t border-border pt-4">
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Webhook Tester (Admin only) */}
        {user?.role === "admin" && (
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Play className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Webhook Tester</h3>
                <p className="text-sm text-slate-400">Test webhook processing (Admin only)</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Webhook Payload (JSON)</Label>
                <Textarea
                  value={testPayload}
                  onChange={(e) => setTestPayload(e.target.value)}
                  rows={15}
                  className="font-mono text-sm bg-background border-border text-foreground"
                />
              </div>

              <Button
                onClick={handleTestWebhook}
                disabled={testing}
                data-testid="test-webhook-btn"
                className="glow-primary"
              >
                {testing ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Play className="w-4 h-4" /> Test Webhook
                  </span>
                )}
              </Button>

              {testResult && (
                <div className={`p-4 rounded-lg border ${testResult.processed ? "bg-emerald-500/10 border-emerald-500/20" : "bg-red-500/10 border-red-500/20"}`}>
                  <p className={`font-semibold ${testResult.processed ? "text-emerald-400" : "text-red-400"}`}>
                    {testResult.processed ? "✓ Webhook Processed" : "✗ Processing Failed"}
                  </p>
                  <pre className="mt-2 text-sm text-slate-300 overflow-x-auto">
                    {JSON.stringify(testResult, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick Reference */}
        <div className="mt-12 p-6 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4">Quick Reference</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-400 mb-1">Webhook Endpoint</p>
              <div className="flex items-center gap-2">
                <code className="text-sm text-blue-400">{API_URL}/api/webhooks/maaly</code>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => copyToClipboard(`${API_URL}/api/webhooks/maaly`)}
                  className="h-6 w-6"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-1">Referral Parameter</p>
              <code className="text-sm text-emerald-400">?ref=AFFILIATE_CODE</code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
